import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, generatePath, useNavigate } from 'react-router';

import {
  UPLOADING_IMAGE,
  UPLOADING_OTHERFILES,
  fetchFolders,
  getFolders,
  getUploadProgress,
  uploadImage,
} from '~/modules/gallery';
import { isLoading as isLoadingSelector } from '~/modules/loading';

import { withRouter } from '~/hooks';

import ImageForm from '../Form';
import styles from './index.scss';

const cx = classNames.bind(styles);

const fileSizeLimits = {
  // in megabytes
  regular: 10,
  premium: 100,
};

function UploadImage(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate ? useNavigate() : props.navigate;
  const currentUser = useSelector((state) => state.currentUser.data);
  const folders = useSelector(getFolders);
  const isLoading = useSelector(
    (state) =>
      isLoadingSelector(state.loading, UPLOADING_IMAGE) ||
      isLoadingSelector(state.loading, UPLOADING_OTHERFILES)
  );
  const uploadProgress = useSelector(getUploadProgress);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [files, setFiles] = useState([]);
  const [thumbnailId] = useState(0); // Remove setThumbnailId, not used

  useEffect(() => {
    if (currentUser) {
      dispatch(fetchFolders(currentUser.username));
    }
  }, [dispatch, currentUser]);

  const onDrop = useCallback((newFiles) => {
    setLoadingFiles(true);
    const processFiles = newFiles.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new window.FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            resolve({
              name: file.name,
              size: file.size,
              type: file.type,
              data: reader.result,
            });
          };
          reader.onerror = reject;
        })
    );
    Promise.all(processFiles).then((values) => {
      setLoadingFiles(false);
      setFiles((prevFiles) => [...prevFiles, ...values]);
    });
  }, []);

  const onSubmit = useCallback(
    (values) => {
      // preferences.setItem("nsfw-last-value", values.nsfw.toString());
      dispatch(
        uploadImage({
          ...values,
          files,
          thumbnail_id: thumbnailId,
          published_at:
            values.uploadMode === 'SCHEDULE' ? values.published_at : null,
        })
      ).then((response) => {
        navigate(
          generatePath('/gallery/image/:imageId', { imageId: response.id })
        );
      });
    },
    [dispatch, files, thumbnailId, navigate]
  );

  const removeFile = useCallback((i) => {
    setFiles((prevFiles) => {
      const newFiles = prevFiles.slice();
      newFiles.splice(i, 1);
      return newFiles;
    });
  }, []);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return (
    <>
      {typeof uploadProgress === 'number' && uploadProgress > 0 && (
        <div className={cx('UploadProgress')}>
          <div
            className={cx('UploadProgress__Bar')}
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}
      {typeof uploadProgress === 'number' && uploadProgress > 0 && (
        <div className={cx('UploadProgress__Label')}>
          {Math.round(uploadProgress)}%
        </div>
      )}

      <ImageForm
        sizeLimit={
          currentUser['premium?']
            ? fileSizeLimits.premium
            : fileSizeLimits.regular
        }
        onDrop={onDrop}
        onSubmit={onSubmit}
        removeFile={removeFile}
        setFiles={setFiles}
        files={files}
        folders={folders}
        isLoading={isLoading || loadingFiles}
      />
    </>
  );
}

export default withRouter(UploadImage);
